// Derivation helpers for the registry. Internal to this folder; not exported
// from the package.
import {
  SIGNAL_CATEGORY_GROUP_MEMBERSHIP,
  SignalCategorySchema,
  type SignalCategoryGroup,
  type SignalCategoryType,
} from './signal-category.registry';

type GroupMembership = typeof SIGNAL_CATEGORY_GROUP_MEMBERSHIP;

export type CategoriesInGroup<G extends SignalCategoryGroup> = Extract<
  SignalCategoryType,
  {
    [C in keyof GroupMembership]: G extends GroupMembership[C][number]
      ? C
      : never;
  }[keyof GroupMembership]
>;

export const isInAnyGroup = (
  category: SignalCategoryType,
  groups: readonly SignalCategoryGroup[],
): boolean =>
  SIGNAL_CATEGORY_GROUP_MEMBERSHIP[category].some((group) =>
    groups.includes(group),
  );

export const categoriesInGroup = <G extends SignalCategoryGroup>(
  group: G,
): [CategoriesInGroup<G>, ...CategoriesInGroup<G>[]] => {
  const [first, ...rest] = SignalCategorySchema.options.filter(
    (category): category is CategoriesInGroup<G> =>
      isInAnyGroup(category, [group]),
  );

  if (!first) {
    throw new Error(`Signal category group ${group} has no categories`);
  }

  return [first, ...rest];
};

export const createCategoryGuard = <C extends SignalCategoryType>(
  categories: readonly C[],
) => {
  const members: ReadonlySet<string> = new Set(categories);

  return (category: SignalCategoryType | string): category is C =>
    members.has(category);
};
